import React, { useState, useEffect } from 'react';
import {
  collection, query, where, onSnapshot, updateDoc, doc, orderBy
} from 'firebase/firestore';
import { db } from './firebase';
import './Notifications.css';

function Notifications({ user, onBack }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, snap => {
      setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return () => unsub();
  }, [user]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true
      });
      // Refresh page to update notification count
      window.location.reload();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      await Promise.all(
        unreadNotifications.map(n =>
          updateDoc(doc(db, 'notifications', n.id), { read: true })
        )
      );
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleDismiss = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        dismissed: true
      });
    } catch (err) {
      console.error('Error dismissing notification:', err);
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'new_account': '👤',
      'assigned_fix': '🔧',
      'assigned_test': '🧪',
      'status_resolved': '✅',
      'comment_added': '💬',
      'defect_cancelled': '🚫',
      'defect_updated': '📝'
    };
    return icons[type] || '🔔';
  };

  const activeNotifications = notifications.filter(n => !n.dismissed);
  const unreadCount = activeNotifications.filter(n => !n.read).length;

  return (
    <div className="notifications-view">
      <header className="header">
        <div className="header-content">
          <div>
            <h1>Wixon & Cross LLC</h1>
            <h2>Notifications</h2>
          </div>
          <div className="header-user">
            <span className="user-email">{user.email}</span>
            <button className="btn btn-secondary btn-small" onClick={onBack}>
              ← Back to Defects
            </button>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="notifications-toolbar">
          <h3>Your Notifications</h3>
          {unreadCount > 0 && (
            <button className="btn btn-primary btn-small" onClick={handleMarkAllAsRead}>
              Mark All as Read ({unreadCount})
            </button>
          )}
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading notifications...</p>
          </div>
        ) : activeNotifications.length === 0 ? (
          <div className="empty-state">
            <p>🔔 No notifications yet!</p>
            <p style={{fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem'}}>
              You'll see updates here when things happen.
            </p>
          </div>
        ) : (
          <div className="notifications-list">
            {activeNotifications.map(notification => (
              <div
                key={notification.id}
                className={`notification-card${notification.read ? '' : ' notification-card--unread'}`}
              >
                <div className="notification-icon">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="notification-content">
                  <div className="notification-message">{notification.message}</div>
                  {notification.defectTitle && (
                    <div className="notification-defect-title">
                      Defect: {notification.defectTitle}
                    </div>
                  )}
                  <div className="notification-time">{formatDateTime(notification.createdAt)}</div>
                </div>
                <div className="notification-actions">
                  {!notification.read && (
                    <button
                      className="notification-action-btn"
                      onClick={() => handleMarkAsRead(notification.id)}
                      title="Mark as read"
                    >
                      ✓
                    </button>
                  )}
                  <button
                    className="notification-action-btn"
                    onClick={() => handleDismiss(notification.id)}
                    title="Dismiss"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <footer className="footer">
        <p>&copy; 2026 Wixon & Cross LLC. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default Notifications;
